<template>
  <form class="step"
        novalidate
        @submit.prevent="submit">
    <template v-for="row in schema.rows">
      <div class="step__row">
        <template v-for="item in row">
          <div class="step__title text_x_large" v-if="item.heading">{{ item.heading }}</div>

          <p class="step__description text_medium" v-if="item.subheading">{{ item.subheading }}</p>

          <ol class="step__list text_medium" v-if="item.list">
            <template v-for="row in item.list">
              <li class="step__list-item">{{row}}</li>
            </template>
          </ol>
          <span v-if="item.field === 'agreementCheckbox'">
            <md-checkbox
                    :name="item.name"
                    v-model="form.privacyPolicy"
                    :id="item.id"
                    :errorMessage="errorMessage('privacy-policy')"
                    :required="item.required">
            </md-checkbox>
            <span v-html="i18n.kyc_i_agree()"></span>
          </span>

          <input-field v-if="item.field === 'text'"
                       v-model="form[item.model]"
                       v-validate="item.validate"
                       class="step__input-field"
                       :name="item.name"
                       :id="item.id"
                       :required="item.required"
                       :label="item.label"
                       :type="item.type"
                       :key='item.id'
                       :errorMessage="errorMessage(item.name)"
                       :disabled="accountState === ACCOUNT_STATES.pending"
          />
          <textarea-field v-if="item.field === 'textarea'"
                          v-model="form[item.model]"
                          v-validate="item.validate"
                          class="step__input-field"
                          :name="item.name"
                          :id="item.id"
                          :required="item.required"
                          :label="item.label"
                          :type="item.type"
                          :key='item.id'
                          :maxlength="item.maxlength"
                          :errorMessage="errorMessage(item.name)"
                          :disabled="accountState === ACCOUNT_STATES.pending"
          />
          <file-field v-if="item.field === 'file'"
                      v-model="documents[item.type]"
                      class="step__file-field"
                      :fileType="item.fileType"
                      :private="item.private"
                      :label="item.label"
                      :type="item.type"
                      :id="item.id"
                      :key='item.id'
                      :disabled="accountState === ACCOUNT_STATES.pending"
          />

          <span  v-if="item.field === 'vue-select' && values[item.values].length">
            {{ item.label }}
            <select-field
                        :name="item.name"
                        :id="item.id"
                        :label="item.label"
                        v-model="form[item.model]"
                        :values="values[item.values]"
                        :options="values[item.values]"
                        :key='item.id'
                        :disabled="accountState === ACCOUNT_STATES.pending"
            />
          </span>
          <date-field-flatpickr v-if="item.field === 'date'"
                         v-model="form[item.model]"
                         v-validate="'required'"
                         :name="item.name"
                         :id="item.id"
                         :required="item.required"
                         :label="item.label"
                         :disableAfter="item.disableAfter"
                         :disableBefore="item.disableBefore"
                         :key='item.id'
                         :errorMessage="errorMessage(item.name)"
                         :disabled="accountState === ACCOUNT_STATES.pending"
                         :enableTime="false"
          />

        </template>
      </div>
    </template>
    <div class="btn-wrap margin-top">
      <md-button type="submit" class="btn md-primary"
              :disabled="isPending || accountState === ACCOUNT_STATES.pending || !form.privacyPolicy"
              v-if="finished">
        {{ i18n.lbl_submit() }}
      </md-button>
      <md-button type="submit" class="btn md-primary"
              :disabled="isPending || accountState === ACCOUNT_STATES.pending"
              v-else>
        {{ i18n.sale_next_step() }}
      </md-button>
    </div>
  </form>
</template>

<script>
  import StepMixin from '../spec/step.mixin'
  import _pick from 'lodash/pick'
  import { ErrorHandler } from '@js/errors/error_handler'
  import { commonEvents } from '@js/events/common_events'
  import { ACCOUNT_STATES } from '@const'
  import { mapGetters } from 'vuex'
  import { vuexTypes } from '@vuex/types'
  import SelectField from '@com/fields/SelectField'

  export default {
    name: 'Step-default',
    mixins: [ StepMixin ],
    components: {SelectField},
    props: ['schema'],
    data: _ => ({
      finished: false,
      ACCOUNT_STATES
    }),
    async created () {
      this.finished = (this.activeStep === this.finalStep)
    },
    computed: {
      ...mapGetters([
        vuexTypes.accountKycData,
        vuexTypes.accountKycDocuments,
        vuexTypes.accountState
      ])
    },

    methods: {
      async submit () {
        if (!await this.isValid()) return
        // if (!this.isValidDocs(this.schema.requiredDocs)) return
        this.disable()
        try {
          await this.uploadDocuments()
          this.$emit(commonEvents.kycUpdateEvent, {
            form: this.form,
            documents: this.documents,
            finalize: true
          })
        } catch (error) {
          ErrorHandler.processUnexpected(error)
        }
        this.enable()
      },

      stubData () {
        if (this.schema && !this.isPending) {
          this.form = _pick(this.kyc, Object.keys(this.schema.form))
          delete this.form.docs
          this.documents = this.accountKycDocuments
        }
      }
    },

    watch: {
      kyc: {
        handler: 'stubData',
        immediate: true
      },
      activeStep (value) {
        this.finished = (value === this.finalStep)
      }
    }
  }
</script>

<style scoped lang="scss">
  @import '../spec/step.scss';
  @import '~scss/variables';
  @import '~scss/mixins';

  .v-select {
    position: relative;
    display: flex;

    & /deep/ .vs__dropdown-toggle {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 300px;
      height: 48px;
      padding: 0 12px;
      @include border(1px, 8px, $col-md-accent);

      &[aria-expanded="true"] {
        & .vs__selected {
          display: none;
        }
      }
    }

    & /deep/ .vs__search {
      display: flex;
      justify-content: space-between;
      margin-top: 6px;
      padding: 6px 12px;
      font-size: 16px;
      background-color: transparent;
      color: $col-text;
      border: none;
    }

    & /deep/ .vs__selected {
      position: absolute;
      top: 8px;
      left: 4px;
      font-size: 16px;

      &-options {
        position: relative;
      }
    }

    & /deep/ .vs__actions {
      > button {
        background: transparent;
        border: none;
      }
      & > svg {
        margin-left: 12px;
      }
      & svg {
        /*fill: white;*/
      }
    }

    & /deep/ ul {
      max-height: 140px;
      position: absolute;
      top: 52px;
      padding: 12px;
      background: $col-background-content;
      z-index: 9;
      overflow: auto;
      @include border(1px, 16px, $col-border)

      /* for Firefox */
      scrollbar-width: thin;
      scrollbar-color: $col-md-primary $col-background;

      /* for Chrome, Edge, and Safari */
      &::-webkit-scrollbar {
        width: 16px;
        height: 12px;
      }

      &::-webkit-scrollbar-track {
        background: $col-background;
        @include border(2px, 12px, $col-background)
      }

      &::-webkit-scrollbar-thumb {
        background-color: $col-md-primary;
        @include border(2px, 12px, $col-background)
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/vue/app/verification/steps/Step.Default.vue